import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  useAssignStationOrganizationMutation,
  useOrganizationStationsQuery,
} from '../../../../../hooks/useDashboardQueries';
import { StationV2 } from '../../../../../types/dashboardV2';
import { parseApiError } from '../../../../../utils/parseApiError';
import { usePaginationState } from '../../../../../hooks/usePaginationState';

export function useOrganizationStationsPage() {
  const { t } = useTranslation();
  const { organizationId = '' } = useParams<{ organizationId: string }>();
  const {
    page: pageIndex,
    rowsPerPage,
    setPage: setPageIndex,
    setRowsPerPage: handleRowsPerPageChange,
  } = usePaginationState();
  const [stationToDetach, setStationToDetach] = useState<StationV2 | null>(null);
  const [isAttachOpen, setIsAttachOpen] = useState(false);
  const [attachError, setAttachError] = useState<string | undefined>();
  const [detachError, setDetachError] = useState<string | undefined>();

  const { data, isLoading, error, refetch } = useOrganizationStationsQuery(
    organizationId,
    pageIndex + 1,
    rowsPerPage,
  );
  const assign = useAssignStationOrganizationMutation();

  const stations = data?.data ?? [];
  // Every station on this page belongs to the org being viewed, so any row
  // carries its name — no extra request just to title the page.
  const organizationName = stations[0]?.organizationName ?? '';

  const confirmDetach = useCallback(async () => {
    if (!stationToDetach) return;
    setDetachError(undefined);
    try {
      await assign.mutateAsync({ stationId: stationToDetach.id, organizationId: null });
      setStationToDetach(null);
    } catch (err) {
      // Stay open so the message is visible instead of looking like a no-op success.
      setDetachError(
        parseApiError(err, t('dashboard.organizationStations.detachFailed')).message,
      );
    }
  }, [assign, stationToDetach, t]);

  const attachStation = useCallback(
    async (stationId: string) => {
      setAttachError(undefined);
      try {
        await assign.mutateAsync({ stationId, organizationId });
        setIsAttachOpen(false);
      } catch (err) {
        // Stay open so the message is visible instead of looking like a no-op success.
        setAttachError(
          parseApiError(err, t('dashboard.organizationStations.attachFailed')).message,
        );
      }
    },
    [assign, organizationId, t],
  );

  // Clear any stale error from a previous attempt whenever the dialog opens
  // or closes, so it never greets the next attach/detach.
  const requestDetach = useCallback((station: StationV2) => {
    setDetachError(undefined);
    setStationToDetach(station);
  }, []);

  const cancelDetach = useCallback(() => {
    setStationToDetach(null);
    setDetachError(undefined);
  }, []);

  const openAttach = useCallback(() => {
    setAttachError(undefined);
    setIsAttachOpen(true);
  }, []);

  const closeAttach = useCallback(() => {
    setIsAttachOpen(false);
    setAttachError(undefined);
  }, []);

  return {
    organizationId,
    organizationName,
    stations,
    total: data?.total ?? 0,
    pageIndex,
    rowsPerPage,
    isLoading,
    error,
    refetch,
    handlePageChange: setPageIndex,
    handleRowsPerPageChange,
    stationToDetach,
    requestDetach,
    cancelDetach,
    confirmDetach,
    detachError,
    isAttachOpen,
    openAttach,
    closeAttach,
    attachStation,
    attachError,
    // One mutation backs both attach and detach, so this is a shared
    // "a write is in flight" flag, not a detach-specific one.
    isSaving: assign.isPending,
  };
}
